import { css, keyframes } from 'styled-components';

/**
 * A utility that adds a shadow dedicated for button-like elements
 * when the element is focused. Mostly for Accessibility purposes.
 */
export const focusShadow = css`
  outline: 2px solid var(--itemFocusFrame);
  outline-offset: 2px;
`;

/**
 * This lovely hack fixes input outline focus styles for Safari. Instead of using themed outline,
 * we are falling back to the default outline styles in Safari. It will respect border-radius
 * but it will ingore pretty much all other styles (like colour and thickness)
 * This is another example of Safari's lack of support for basic CSS.
 * Original issue https://bugs.webkit.org/show_bug.cgi?id=20807 was created in 2008. This should give an idea
 * if we can expect quick resolution on the Apple's team.
 * The hack below targets ANY version of Safari and Safari only. Normal browsers should not be affected.
 */
export const fixFocusInSafari = css`
  @media not all and (resolution >= 0.001dpcm) {
    @supports (-webkit-appearance: none) {
      &:focus {
        outline: -webkit-focus-ring-color auto 2px;
      }
    }
  }
`;

/**
 * A utility that hides and element without using `display: none`.
 * This is useful when you want to hide content, but still make it
 * interactive (for Accessibility purposes for example)
 * Inspired by Chakra UI approach
 */
export const visuallyHidden = css`
  border: 0;
  clip: rect(0, 0, 0, 0);
  height: 1px;
  width: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  white-space: nowrap;
  position: absolute;
`;

/** A css utility for filling the whole screen with a fixed position */
export const fullPageFixedPosition = css`
  position: fixed;
  inset: 0;
`;

export const fadeInAnimation = keyframes`
  from {opacity: 0;}
  to {opacity: 1;}
`;

export const fadeOutAnimation = keyframes`
  from {opacity: 1;}
  to {opacity: 0;}
`;

export const fadeInOutTransition = css`
  &.enter {
    display: flex;
    animation-name: ${fadeInAnimation};
    animation-duration: 300ms;
    animation-timing-function: ease-in-out;
  }

  &.exit {
    display: flex;
    animation-name: ${fadeOutAnimation};
    animation-duration: 300ms;
    animation-timing-function: ease-in-out;
  }

  &.enter-done {
    display: flex;
  }

  &.exit-done {
    display: none;
  }
`;

export const opacityEaseFunction = 'cubic-bezier(0.4, 0, 0.6, 1)';

export const transformEaseFunction = 'cubic-bezier(0.4, 0, 0.2, 1)';

/** Default opacity transition shared between controls subcomponents */
export const opacityTransition = css`
  transition: opacity 400ms ${opacityEaseFunction};
`;
